
import { Element } from "./element";
import { rem_to_px } from "../px";
import { transform_x, scale_about_pos, create_translate_matrix } from "../../../lib/matrix";
import { sprintf } from 'sprintf-js';

const lerp = (t, from, to) => {
  return (1-t) * from + t * to;
}

const MONTH_LUT = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "July",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec"
];

/**
 * Renders an x-axis into a chart
 */
class XAxisRt extends Element {

  constructor(position_cb, sim_days) {
    super(position_cb);

    this.sim_days = sim_days;
    this.data_from = sim_days[0].t_yearly;
    this.data_to = sim_days[sim_days.length - 1].t_yearly;

  }

  /**
   * Given a list of dates, return a dict of dates to t_yearly values.
   * Returns the closest one.
   * @param date_str [YYYY-MM-DD]
   * @return { YYYY-MM-DD -> float }
   */
  collectTicks = (date_strs) => {
    const result = {};
    let curr_index = 0;

    date_strs.forEach(date_str => {
      while(curr_index < this.sim_days.length && this.sim_days[curr_index].date < date_str) {
        curr_index++;
      }

      if (curr_index < this.sim_days.length) {
        result[date_str] = this.sim_days[curr_index].t_yearly;
      } else {
        // Assign the last day
        result[date_str] = this.sim_days[this.sim_days.length - 1].t_yearly;
      }
    });

    return result;
  }

  collectMonthTicks = () => {
    const get_month_year = date_str => {
      const tt = date_str.split("-");
      return [
        parseInt(tt[0], 10),
        parseInt(tt[1], 10),
      ];
    }
    let [ year, month ] = get_month_year(this.sim_days[0].date);

    const result = [];

    for(let i=0 ; i < this.sim_days.length; i++) {
      let [curr_year, curr_month ] = get_month_year(this.sim_days[i].date);
      if (curr_year !== year || curr_month !== month) {
        result.push({
          year: curr_year,
          month: curr_month,
          month_str: MONTH_LUT[curr_month],
          t_yearly: this.sim_days[i].t_yearly,
        })
        year = curr_year;
        month = curr_month;
      }
    }
    return result;
  }

  collectDayTicks = () => {
    const result = [];

    for (let i=0; i < this.sim_days.length; i++) {
      let [ y, m, d ] = this.sim_days[i].date.split("-");
      const month = parseInt(m, 10);
      const day = parseInt(d, 10);
      result.push({
        year: parseInt(y, 10),
        month,
        day,
        day_label: d,
        month_day_label: sprintf("%s-%s", MONTH_LUT[month], day),
        t_yearly: this.sim_days[i].t_yearly,
      })
    }
    return result;
  }

  renderTick = (ctx, x, label, bottomOffset = 0.5, topOffset=0., text_offset = 0.) => {
    const topLength = rem_to_px(topOffset);
    const bottomLength = rem_to_px(bottomOffset);

    ctx.beginPath();
    ctx.moveTo(x, this.top - topLength);
    ctx.lineTo(x, this.top + bottomLength);
    ctx.stroke();

    ctx.textAlign = "center";
    ctx.fillStyle = "#000";
    ctx.fillText(label, x, this.top + bottomLength * 2.5 + text_offset);
  };

  yearToPix = year => {
    return (year - this.view_from)/(this.view_to - this.view_from)
      * (this.right - this.left) + this.left;
  };

  /**
   * Returns the pixel px in coordinates normalized to the
   * span of time.
   */
  pxToNorm = (px, view_matrix_inv) => {
    const px_norm = (px - this.left) / (this.right - this.left);
    const from_t = transform_x(view_matrix_inv, 0);
    const to_t = transform_x(view_matrix_inv, 1);
    return lerp(px_norm, from_t, to_t);
  }

  /**
   * Returns the pixel px in year coordinates
   */
  pxToYear = (px, view_matrix_inv) => {
    const px_norm = this.pxToNorm(px, view_matrix_inv);
    return lerp(px_norm, this.data_from, this.data_to);
  }

  calcScaleMatrix = (grab_x, drag_x, view_matrix_inv) => {
    const scale_mult = 5;
    const x_normalized = this.pxToNorm(grab_x, view_matrix_inv);
    const view_sx = view_matrix_inv.diagonal().get([0]);
    const sx = 1.0 + (drag_x - grab_x) / this.width() * scale_mult * view_sx;
    return scale_about_pos(sx, 1, x_normalized, 0);
  }

  calcPanMatrix = (grab_x, drag_x, view_matrix_inv) => {
    const view_sx = view_matrix_inv.diagonal().get([0]);
    const dx = (drag_x - grab_x) / this.width() * view_sx;
    return create_translate_matrix(dx, 0);
  }

  render = (canvas, view_matrix_inv) => {
    this.updatePos();

    // top line
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle="#000";
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.moveTo(this.left, this.top);
    ctx.lineTo(this.right, this.top);
    ctx.stroke();

    const from_t = transform_x(view_matrix_inv, 0);
    const to_t = transform_x(view_matrix_inv, 1);

    this.view_from = lerp(from_t, this.data_from, this.data_to);
    this.view_to = lerp(to_t, this.data_from, this.data_to);

    const view_density =  this.width() / (this.view_to - this.view_from) / 365;

    const year_from = Math.floor(this.view_from);
    const year_to = Math.ceil(this.view_to);

    // Render year ticks
    for(let year = year_from; year <= year_to; year++) {
      this.renderTick(ctx, this.yearToPix(year), sprintf("%d", year), 0.5, 1);
    }

    // Render month ticks
    this.collectMonthTicks().forEach(monthTick => {
      this.renderTick(ctx, this.yearToPix(monthTick.t_yearly), monthTick.month_str, 0.6, 0,4);
    })

    const day_offset = -3;

    if (view_density > 15) {
      // Render all day ticks
      this.collectDayTicks().forEach(dayTick => {
        this.renderTick(ctx, this.yearToPix(dayTick.t_yearly), dayTick.day_label, 0.5, 0.1, day_offset);
      })
    } else if (view_density > 10) {
      this.collectDayTicks().forEach((dayTick, index) => {
        const major = index % 4 === 0;
        this.renderTick(
          ctx,
          this.yearToPix(dayTick.t_yearly),
          major ? dayTick.day_label: "",
          major ? 0.5 : 0.25,
          major ? 0.1: 0,
          day_offset);
      })
    } else if (view_density > 5) {
      this.collectDayTicks().forEach((dayTick, index) => {
        const minor = index % 8 === 0;
        const major = index % 4 === 0;
        if (major || minor) {
          this.renderTick(
            ctx,
            this.yearToPix(dayTick.t_yearly),
            minor ? "": dayTick.day_label,
            minor ? 0.25 : 0.5,
            minor ? 0 : 0.1,
            day_offset);
        }

      })
    }



  }
}

export { XAxisRt };
